import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import core from '@gin-betting/core';
import { includes } from 'lodash';
import { createStructuredSelector } from 'reselect';

import {
    DEFAULT_STREAM_URL,
    ExtendedRouts,
    VIEWABLE_SPORT_LIST,
} from 'app-constants';
import { useMount, usePrevious } from 'hooks';
import { State } from 'types';
import { changeViewerMode, resetViewerMode, unpinStream } from '../../actions';
import { ViewerMode } from '../../constants';
import {
    selectIsFullScreen,
    selectLastPlayedStreamMatchId,
    selectPinnedStreamMatchId,
} from '../../selectors';
import useStream from './useStream';

const {
    services: { getHistory },
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;

interface Input {
    matchId: string | null;
    url: string | null;
    betradarId: string | null;
    sportId?: string;
    hasMatchLog: boolean;
    isMatchTrackerDisabled: boolean;
}

interface Output extends ReturnType<typeof useStream> {
    autoplay: boolean;
    isFullscreenActive: boolean;
    onChangeViewerModeToStream: VoidFn;
    onChangeViewerModeToMatchTracker: VoidFn;
    betradarId: string | null;
    isValidSportId: boolean;
    hasMatchLog: boolean;
    goToAftermatch: () => void;
    isSmall: boolean;
}

const selectProps = createStructuredSelector({
    isFullscreenActive: selectIsFullScreen,
    lastPlayedStreamMatchId: selectLastPlayedStreamMatchId,
    pinnedStreamMatchId: selectPinnedStreamMatchId,
    isSmall: makeViewportSelector('lessThanOrEqual', 'small'),
});

const useStreamControls = ({
    matchId,
    url,
    betradarId,
    sportId,
    hasMatchLog,
    isMatchTrackerDisabled,
}: Input): Output => {
    const dispatch = useDispatch();
    const {
        onStreamUnpin,
        onTogglePlayStatus,
        onToggleStreamFullscreen,
        viewerMode,
        initPlayer,
        player,
    } = useStream({ matchId });

    const {
        isFullscreenActive,
        lastPlayedStreamMatchId,
        pinnedStreamMatchId,
        isSmall,
    } = useSelector((s: State) => selectProps(s, {}));

    const [autoplay] = useState<boolean>(
        () => matchId === lastPlayedStreamMatchId
    );

    const isValidSportId = !!sportId && includes(VIEWABLE_SPORT_LIST, sportId);

    const prevMatchId = usePrevious(matchId);

    useMount(() => {
        if (matchId !== pinnedStreamMatchId) return;

        dispatch(unpinStream());
    });

    const onChangeViewerModeToMatchTracker = useCallback(() => {
        if (!betradarId || !isValidSportId || isMatchTrackerDisabled) return;
        dispatch(changeViewerMode(ViewerMode.MATCH_TRACKER));
    }, [betradarId, isValidSportId, isMatchTrackerDisabled, dispatch]);

    const onChangeViewerModeToStream = useCallback((): boolean => {
        const isSetStreamViewerModeAvailable =
            !!url && url !== DEFAULT_STREAM_URL;

        if (isSetStreamViewerModeAvailable) {
            dispatch(changeViewerMode(ViewerMode.STREAM));
        }

        return isSetStreamViewerModeAvailable;
    }, [dispatch, url]);

    useEffect(() => {
        if (!matchId || matchId === prevMatchId) return;

        if (!onChangeViewerModeToStream()) {
            onChangeViewerModeToMatchTracker();
        }
    }, [
        dispatch,
        matchId,
        prevMatchId,
        onChangeViewerModeToStream,
        onChangeViewerModeToMatchTracker,
    ]);

    useEffect(() => {
        if (viewerMode === ViewerMode.MATCH_TRACKER && isMatchTrackerDisabled) {
            dispatch(resetViewerMode());
        }
    }, [dispatch, viewerMode, isMatchTrackerDisabled]);

    const goToAftermatch = useCallback(() => {
        if (!hasMatchLog) return;

        // we can use slugs for this link later
        const history = getHistory();
        history.push(`${ExtendedRouts.AFTERMATCH}${matchId}`);
    }, [matchId, hasMatchLog]);

    return {
        initPlayer,
        player,
        autoplay,
        isFullscreenActive,
        onTogglePlayStatus,
        onStreamUnpin,
        onToggleStreamFullscreen,
        viewerMode,
        betradarId,
        isValidSportId,
        onChangeViewerModeToStream,
        onChangeViewerModeToMatchTracker,
        hasMatchLog,
        goToAftermatch,
        isSmall,
    };
};

export default useStreamControls;
